import { Input, Select, assets } from '@pulseops/common'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import React, { useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './PDTable'
import { View, Text, ActivityIndicator } from 'react-native'
import { borderedInputStyle, borderedSelectStyle } from '../styles/policyDispatchingStyles'
import PolicyDispatchingDialog from './PolicyDispatchingDialog'
import PDChangeInfoDialog from './PDChangeInfoDialog'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import {
  PolicyDispatchingService,
  AuthorizedPerson,
  StockOutAgentGroupChangeDatePayload,
  StockOutAgentGroupContract,
  StockOutAgentGroupDeliveryPayload,
  StockOutAgentGroupSearchBody,
  AuthorizationRow
} from '../services/PolicyDispatchingService'
import { getStatusLabel, getStatusStyle, getStatusValue } from '../utils/policyDispatchingUtils'
import ChoosePersonDialog from './ChoosePersonDialog'

const headCell = [
  { id: 1, label: 'Số hợp đồng' },
  { id: 2, label: 'Ngày phát hành đầu tiên' },
  { id: 3, label: 'Ngày phát hành' },
  { id: 4, label: 'Văn phòng' },
  { id: 5, label: 'Mã số đại lý' },
  { id: 6, label: 'Tên đại lý' },
  { id: 7, label: 'Ngày giao hợp đồng' },
  { id: 8, label: 'Người nhận' },
  { id: 9, label: 'Tình trạng' },
  { id: 10, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'contractNumber' },
  { id: 2, label: 'firstIssueDate' },
  { id: 3, label: 'issueDate' },
  { id: 4, label: 'office' },
  { id: 5, label: 'agentNumber' },
  { id: 6, label: 'agentName' },
  { id: 7, label: 'deliveryDate' },
  { id: 8, label: 'receiver' },
  { id: 9, label: 'status' },
  { id: 10, label: 'remark' }
]

const headAuthorizedCell = [
  { id: 1, label: 'Số ủy quyền' },
  { id: 2, label: 'Người ủy quyền' },
  { id: 3, label: 'Người được ủy quyền' },
  { id: 4, label: 'Ngày có hiệu lực' },
  { id: 5, label: 'Hạn dùng' },
  { id: 6, label: 'Xác nhận bởi' }
]

const rowAuthorizedCell = [
  { id: 1, label: 'id' },
  { id: 2, label: 'authorizationAgent' },
  { id: 3, label: 'authorizedPerson' },
  { id: 4, label: 'effectiveDate' },
  { id: 5, label: 'expiryDate' },
  { id: 6, label: 'approver' }
]

const statusOptions = [
  { value: 'AVAILABLE', label: 'Còn trong kho' },
  { value: 'IN_USED', label: 'Đã giao' },
  { value: 'RETURNED', label: 'Đã trả lại' }
]

const receiverOptions = [
  { value: 'leader', label: 'Trưởng nhóm' },
  { value: 'branchLeader', label: 'Trưởng ban' },
  { value: 'authorizedPerson', label: 'Người được ủy quyền' },
  { value: 'other', label: 'Khác' }
]

type FormValues = {
  agentGroup: string
  leaderCode: string
  leaderName: string
  groupStatus: string
  groupType: string
  salesBranch: string
  area: string
  office: string
  zone: string
  receiverOption: { value: string; label: string } | null
  note: string
  policyStatus: any
  receiver: string
}

const getSearchBody = (response: { body?: StockOutAgentGroupSearchBody; responseStatus?: unknown }) =>
  response.body?.informationGroup ? response.body : null

const unwrapZioEither = (response: unknown): unknown =>
  response && typeof response === 'object' && '_tag' in response
    ? (response as any)._tag === 'Right'
      ? (response as any).right
      : null
    : response

const formatDate = (date: Date | null) => {
  if (!date) {
    return null
  }

  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export type PDStockOutAgentGroupHandle = {
  handleDeliveryContracts: () => void
  handleCancelContracts: () => void
  handleAdjustDeliveryDate: () => void
}

function PDStockOutAgentGroup(
  { styles }: { styles: { [key: string]: any } },
  ref: React.Ref<PDStockOutAgentGroupHandle>
) {
  const { officeCode } = usePolicyDispatchingOffice()
  const { control, handleSubmit, setValue, watch, getValues } = useForm<FormValues>({
    defaultValues: {
      agentGroup: '',
      leaderCode: '',
      leaderName: '',
      groupStatus: '',
      groupType: '',
      salesBranch: '',
      area: '',
      office: '',
      zone: '',
      receiverOption: null,
      receiver: '',
      note: '',
      policyStatus: statusOptions[0]
    }
  })
  const [rows, setRows] = React.useState<StockOutAgentGroupContract[]>([])
  const [selectedRows, setSelectedRows] = React.useState<StockOutAgentGroupContract[]>([])
  const [allRowsSelected, setAllRowsSelected] = React.useState(false)
  const [pageInfo, setPageInfo] = React.useState<StockOutAgentGroupSearchBody['page'] | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [openModal, setOpenModal] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [receiverName, setReceiverName] = React.useState({ leader: '', branchLeader: '' })
  const [authorizationDialogOpen, setAuthorizationDialogOpen] = React.useState(false)
  const [authorizationRows, setAuthorizationRows] = React.useState<AuthorizationRow[]>([])
  const [authorizationLoading, setAuthorizationLoading] = React.useState(false)
  const [openChangeDeliveryDateDialog, setOpenChangeDeliveryDateDialog] = React.useState(false)
  const lastSearchValues = useRef<FormValues | null>(null)

  const handleReceiverOptionChange = (
    option: FormValues['receiverOption'],
    onChange: (value: FormValues['receiverOption']) => void
  ) => {
    onChange(option)

    const receiverByOption = {
      leader: receiverName.leader,
      branchLeader: receiverName.branchLeader,
      authorizedPerson: '',
      other: ''
    }

    setValue('receiver', option ? receiverByOption[option.value as keyof typeof receiverByOption] : '')
  }

  const search = (values: FormValues, page: number, resetSelection = false) => {
    if (!officeCode) {
      setModalInfo({ title: 'Văn phòng', message: 'Vui lòng chọn văn phòng trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    if (!values.agentGroup) {
      setModalInfo({ title: 'Nhóm đại lý', message: 'Vui lòng nhập nhóm đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchStockOutAgentGroup({
        officeCode,
        agentGroup: values.agentGroup,
        status: values.policyStatus.value,
        page: String(page)
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          const body = getSearchBody(response)
          if (body) {
            setValue('leaderCode', body.informationGroup.leaderCode)
            setValue('leaderName', body.informationGroup.leaderName)
            setValue('groupStatus', body.informationGroup.status)
            setValue('groupType', body.informationGroup.type)
            setValue('salesBranch', body.informationGroup.salesBranch)
            setValue('area', body.informationGroup.area)
            setValue('office', body.informationGroup.office)
            setValue('zone', body.informationGroup.zone)
            setReceiverName({
              leader: body.informationReceiver.leaderName,
              branchLeader: body.informationReceiver.leaderBranchName
            })
            const preserveAllSelected = !resetSelection && allRowsSelected
            const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.contractNumber))
            if (resetSelection) {
              setSelectedRows([])
              setAllRowsSelected(false)
            }
            setRows(
              body.listContract?.map((item) => ({
                ...item,
                status: getStatusLabel(item.status),
                checked: preserveAllSelected || selectedKeys.has(item.contractNumber)
              }))
            )
            setPageInfo(body.page)
          } else {
            setValue('leaderCode', '')
            setValue('leaderName', '')
            setValue('groupStatus', '')
            setValue('groupType', '')
            setValue('salesBranch', '')
            setValue('area', '')
            setValue('office', '')
            setValue('zone', '')
            setModalInfo({
              title: 'Tìm kiếm',
              message: 'Không tìm thấy thông tin được yêu cầu!'
            })
            setOpenModal(true)
            setRows([])
            setSelectedRows([])
            setAllRowsSelected(false)
            setPageInfo(null)
          }
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Đã có lỗi xảy ra trong quá trình tìm kiếm. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => {
    lastSearchValues.current = values
    search(values, 0, true)
  })

  const handlePageChange = (page: number) => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, page)
    }
  }

  const refreshSearch = () => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, 0, true)
    }
  }

  const handleOpenAuthorizationDialog = () => {
    const agentGroup = watch('agentGroup')
    if (!agentGroup) {
      setModalInfo({ title: 'Nhóm đại lý', message: 'Vui lòng nhập nhóm đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }
    setAuthorizationDialogOpen(true)
    setAuthorizationLoading(true)

    pipe(
      PolicyDispatchingService.getAuthorizedPersons(agentGroup),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setAuthorizationRows(
            response.body.items.map((item: AuthorizedPerson) => ({
              id: item.authorizationLetterId,
              authorizationAgent: item.agentNumber,
              authorizedPerson: item.authorizedPerson,
              effectiveDate: item.effectiveFromDate,
              expiryDate: item.effectiveToDate,
              approver: item.approver
            }))
          )
          setAuthorizationLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setAuthorizationRows([])
          setAuthorizationLoading(false)
          setModalInfo({
            title: 'Danh sách ủy quyền',
            message: 'Không thể tải danh sách ủy quyền. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleDeliveryContracts = () => {
    if (selectedRows.length === 0) {
      setModalInfo({ title: 'Giao hợp đồng', message: 'Vui lòng chọn ít nhất một hợp đồng để giao.' })
      setOpenModal(true)
      return
    }

    const receiver = watch('receiver')
    const note = watch('note')
    if (!receiver) {
      setModalInfo({ title: 'Giao hợp đồng', message: 'Vui lòng nhập tên người nhận trước khi giao.' })
      setOpenModal(true)
      return
    }

    const payload: StockOutAgentGroupDeliveryPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentNumber: row.agentNumber,
      status: getStatusValue(row.status),
      receiver,
      remark: note || row.remark || ''
    }))
    const condition = payload.every((item) => item.status === 'AVAILABLE')
    if (!condition) {
      setModalInfo({
        title: 'Giao hợp đồng',
        message: 'Chỉ có thể giao các hợp đồng đang ở trạng thái "Còn trong kho". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deliveryStockOutAgentGroup(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Giao hợp đồng',
            message: 'Giao hợp đồng thành công.'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Giao hợp đồng',
            message: 'Không thể giao hợp đồng. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleCancelContracts = () => {
    if (selectedRows.length === 0) {
      setModalInfo({ title: 'Hủy giao hợp đồng', message: 'Vui lòng chọn ít nhất một hợp đồng để hủy giao.' })
      setOpenModal(true)
      return
    }

    const note = watch('note')
    const payload: StockOutAgentGroupDeliveryPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentNumber: row.agentNumber,
      status: getStatusValue(row.status),
      receiver: row.receiver,
      remark: note || row.remark || ''
    }))
    const condition = payload.every((item) => item.status === 'IN_USED')
    if (!condition) {
      setModalInfo({
        title: 'Hủy giao hợp đồng',
        message: 'Chỉ có thể hủy giao các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.cancelStockOutAgentGroup(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Hủy giao hợp đồng',
            message: 'Hủy giao hợp đồng thành công'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Hủy giao hợp đồng',
            message: 'Không thể hủy giao hợp đồng. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleAdjustDeliveryDate = () => {
    if (selectedRows.length === 0) {
      setModalInfo({
        title: 'Điều chỉnh ngày giao',
        message: 'Vui lòng chọn ít nhất một hợp đồng để điều chỉnh ngày giao.'
      })
      setOpenModal(true)
      return
    }

    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'IN_USED')
    if (!condition) {
      setModalInfo({
        title: 'Điều chỉnh ngày giao',
        message: 'Chỉ có thể điều chỉnh ngày giao cho các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }

    setOpenChangeDeliveryDateDialog(true)
  }

  const handleChangeDeliveryDate = (newDeliveryDate: Date, remark: string) => {
    const payload: StockOutAgentGroupChangeDatePayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      agentNumber: row.agentNumber,
      status: getStatusValue(row.status),
      receiver: row.receiver,
      remark: remark || row.remark || '',
      newDeliveryDate: formatDate(newDeliveryDate) || ''
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.changeDateStockOutAgentGroup(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Điều chỉnh ngày giao',
            message: 'Điều chỉnh ngày giao thành công'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('receiverOption', null)
          setValue('receiver', '')
          setValue('note', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Điều chỉnh ngày giao',
            message: 'Không thể điều chỉnh ngày giao. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }
  React.useImperativeHandle(ref, () => ({ handleDeliveryContracts, handleCancelContracts, handleAdjustDeliveryDate }))

  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 24 } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      <PDChangeInfoDialog
        visible={openChangeDeliveryDateDialog}
        selectedCount={selectedRows.length}
        currentDates={selectedRows.map((row) => row.deliveryDate)}
        stockInDates={selectedRows.map((row) => row.stockInDate)}
        action="changeDate"
        title="Điều chỉnh ngày giao"
        dateLabel="Ngày giao mới"
        duplicateDateMessage="Ngày giao mới không được trùng với ngày giao hiện tại."
        onClose={() => setOpenChangeDeliveryDateDialog(false)}
        onConfirm={(value, remark) => handleChangeDeliveryDate(value as Date, remark)}
      />
      <View
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 24, ...styles.column_1_4 } as any}
      >
        <Controller
          name="agentGroup"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Nhóm'}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              containerStyle={{ flex: 1 }}
              inputStyle={borderedInputStyle}
              filled
              required
              onChange={(e) => onChange(e.toUpperCase())}
            />
          )}
        />
        <button style={styles.submitButton} type="button" onClick={handleSearch} disabled={isLoading}>
          <span style={styles.mr_5}>
            <assets.SearchWhite />
          </span>
          Tìm
        </button>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="leaderCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Mã trưởng nhóm'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_2}>
          <Controller
            name="leaderName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên trưởng nhóm'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="groupStatus"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tình trạng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_2}>
          <Controller
            name="groupType"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Loại'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="salesBranch"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ban'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_2}>
          <Controller
            name="area"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Khu vực'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="office"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Văn phòng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_2}>
          <Controller
            name="zone"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Vùng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <View>
          <Text style={styles.subTitle}> - Thông tin người nhận</Text>
        </View>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="receiverOption"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Người nhận'}
                options={receiverOptions}
                placeholder={'Chọn người nhận'}
                onChange={(e) => handleReceiverOptionChange(e, onChange)}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                disabled={receiverName.leader === '' && receiverName.branchLeader === ''}
                required
              />
            )}
          />
        </View>

        <View style={styles.column_1_4}>
          <Controller
            name="receiver"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên người nhận'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled={watch('receiverOption')?.value !== 'other'}
                alwayShowUnderline
                required
              />
            )}
          />
        </View>
      </View>

      {watch('receiverOption')?.value === 'authorizedPerson' && (
        <button
          type="button"
          onClick={handleOpenAuthorizationDialog}
          style={
            {
              border: 0,
              background: 'none',
              color: '#ed1b2e',
              textDecoration: 'underline',
              padding: 0,
              width: 'fit-content',
              fontSize: 16,
              marginTop: '-16px',
              fontWeight: '500'
            } as any
          }
        >
          Danh sách ủy quyền
        </button>
      )}

      <View style={{ width: 'calc(50% - 12px)' } as any}>
        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Ghi chú'}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              containerStyle={{ flex: 1 }}
              multiline
              numberOfLines={5}
              inputStyle={{ padding: 10, marginTop: 4 }}
              filled
              alwayShowUnderline
            />
          )}
        />
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <View>
          <Text style={styles.subTitle}> - Danh sách hợp đồng</Text>
        </View>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={{ width: '15%' } as any}>
        <Controller
          name="policyStatus"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Select
              label={'Tình trạng'}
              options={statusOptions}
              placeholder={'Chọn tình trạng'}
              menuPlacement="below"
              onChange={(e) => {
                onChange(e)
                const searchValues = { ...getValues(), policyStatus: e }
                lastSearchValues.current = searchValues
                search(searchValues, 0, true)
              }}
              onBlur={onBlur}
              value={value}
              key={value?.value}
              errorMessage={error?.message}
              containerStyles={borderedSelectStyle}
              filled
              required
            />
          )}
        />
      </View>

      <View>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          hasCheckbox
          hasCheckAll
          getStyle={(cell, value) => (cell === 'status' ? getStatusStyle(value) : {})}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row) => row.contractNumber))
              const selectionsFromOtherPages = currentSelectedRows.filter(
                (row) => !currentPageKeys.has(row.contractNumber)
              )
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={(selected) => {
            setAllRowsSelected(selected)
            setRows((currentRows) => currentRows.map((row) => ({ ...row, checked: selected })))

            if (!selected) {
              setSelectedRows([])
              setAllRowsSelected(false)
              return
            }

            if (!officeCode || !lastSearchValues.current || !pageInfo || pageInfo.totalPages <= 1) {
              setSelectedRows(rows)
              return
            }

            const values = lastSearchValues.current
            setIsLoading(true)
            Promise.all(
              Array.from({ length: pageInfo.totalPages }, (_, page) =>
                pipe(
                  PolicyDispatchingService.searchStockOutAgentGroup({
                    officeCode,
                    agentGroup: values.agentGroup,
                    status: values.policyStatus.value,
                    page: String(page)
                  }),
                  ZIO.runPromise({})
                )
              )
            )
              .then((responses) => {
                const allContracts = responses.flatMap((response) => {
                  const body = getSearchBody(unwrapZioEither(response) as any)
                  return body?.listContract || []
                })
                setSelectedRows(allContracts.length > 0 ? allContracts : rows)
                setIsLoading(false)
              })
              .catch(() => {
                setSelectedRows(rows)
                setIsLoading(false)
                setModalInfo({
                  title: 'Chọn tất cả',
                  message: 'Không thể tải toàn bộ danh sách hợp đồng. Đã chọn các hợp đồng ở trang hiện tại.'
                })
                setOpenModal(true)
              })
          }}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={
            pageInfo
              ? {
                  ...pageInfo,
                  onPageChange: handlePageChange
                }
              : undefined
          }
        />
      </View>

      <ChoosePersonDialog
        open={authorizationDialogOpen}
        loading={authorizationLoading}
        title={'Danh sách giấy ủy quyền'}
        headCell={headAuthorizedCell}
        rowCell={rowAuthorizedCell}
        keySubmit="authorizedPerson"
        rows={authorizationRows}
        onClose={() => setAuthorizationDialogOpen(false)}
        onSelect={(authorizedPerson) => {
          setValue('receiver', authorizedPerson)
          setAuthorizationDialogOpen(false)
        }}
      />
    </View>
  )
}

export default React.forwardRef(PDStockOutAgentGroup)
